import {
  createLocation,
  deleteLocation,
  getSiteBySlug,
  orUndefined,
  type SiteResponse,
} from '@lms/api-client/backend';
import { z } from 'zod';

// Ports app/[brand]/admin/locations/_components/actions.schema.ts
export const addLocationSchema = z.object({
  name: z.preprocess((v) => (v === '' ? undefined : v), z.string().min(1)),
  slug: z.preprocess((v) => (v === '' ? undefined : v), z.string().min(1)),
  template: z.preprocess(
    (v) =>
      v === '' || v === undefined ? undefined : Number.parseInt(`${v}`, 10),
    z.number().gte(0).optional(),
  ),
});

export interface AddLocationInput {
  portfolio: SiteResponse;
  name: string;
  slug: string;
  template?: string;
}

export type AddLocationResult =
  | { status: 'success'; location: SiteResponse }
  | {
      status: 'error';
      fieldErrors?: { name?: string; slug?: string; template?: string };
      formErrors?: string[];
    };

/**
 * Create a location from an optional site-template. Ports `addLocation` from the
 * Next app's _components/actions.ts: the server action validated with conform +
 * zod, revalidated the list path and `redirect`ed on success. Here it validates
 * inline and returns a result the caller acts on (navigate + SWR mutate).
 */
export async function addLocation(
  input: AddLocationInput,
): Promise<AddLocationResult> {
  const submission = addLocationSchema.safeParse({
    name: input.name,
    slug: input.slug,
    template: input.template,
  });

  if (!submission.success) {
    const fieldErrors = submission.error.flatten().fieldErrors;
    return {
      status: 'error',
      fieldErrors: {
        name: fieldErrors.name?.[0],
        slug: fieldErrors.slug?.[0],
        template: fieldErrors.template?.[0],
      },
    };
  }

  const portfolio = input.portfolio;
  if (!portfolio || portfolio.siteType !== 'portfolio') {
    return { status: 'error', formErrors: ['Unknown portfolio'] };
  }

  try {
    const { name, slug, template: templateId } = submission.data;

    if ((await orUndefined(getSiteBySlug(slug))) !== undefined) {
      return { status: 'error', fieldErrors: { slug: 'Slug is already used' } };
    }

    const location = await createLocation({
      portfolioId: portfolio.id,
      name,
      slug,
      templateId: templateId && templateId > 0 ? templateId : undefined,
    });
    return { status: 'success', location };
  } catch (e) {
    return { status: 'error', formErrors: [`${e}`] };
  }
}

/**
 * Delete a location. Ports `removeLocation` from _components/actions.ts; the
 * caller revalidates the list via SWR mutate (the Next action used
 * `revalidatePath`).
 */
export async function removeLocation(
  locationId: number,
): Promise<{ ok: true } | { error: string }> {
  try {
    await deleteLocation(locationId);
    return { ok: true };
  } catch (e) {
    return { error: `${e}` };
  }
}
